/**
 * UpgradeMenu - Player upgrade and progression menu
 */

import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../../state/store';
import { playUIClick, playUIHover } from '../../game/audio/SoundEffects';
import { upgradeSystem, type UpgradeDefinition } from '../../game/systems/UpgradeSystem';

interface Upgrade {
  id: string;
  name: string;
  description: string;
  currentLevel: number;
  maxLevel: number;
  cost: number;
  stat: 'health' | 'speed' | 'stamina' | 'battery';
}

interface UpgradeMenuProps {
  onClose?: () => void;
  onPurchase?: (upgradeId: string) => void;
  visible?: boolean;
  embedded?: boolean; // When true, renders without fullscreen overlay
}

export const UpgradeMenu: React.FC<UpgradeMenuProps> = ({
  onClose,
  onPurchase,
  visible = false,
  embedded = false
}) => {
  // Get player XP from Redux state
  const playerXP = useSelector((state: RootState) => state.player.xp);
  const playerLevel = useSelector((state: RootState) => state.player.level);

  const [hoveredUpgrade, setHoveredUpgrade] = useState<string | null>(null);
  const [selectedUpgrade, setSelectedUpgrade] = useState<string | null>(null);

  if (!visible) return null;

  // Get upgrades from UpgradeSystem
  const systemUpgrades = upgradeSystem.getUpgrades();

  // Map to UI format
  const upgrades: Upgrade[] = systemUpgrades.map(upgrade => ({
    id: upgrade.id,
    name: upgrade.name,
    description: upgrade.description,
    currentLevel: upgrade.currentLevel,
    maxLevel: upgrade.maxLevel,
    cost: upgrade.cost,
    stat: upgrade.stat as 'health' | 'speed' | 'stamina' | 'battery'
  }));

  const handlePurchase = (upgradeId: string) => {
    playUIClick();
    const success = upgradeSystem.purchaseUpgrade(upgradeId);
    if (success) {
      console.log(`✅ Successfully purchased upgrade: ${upgradeId}`);
      onPurchase?.(upgradeId);
    } else {
      console.warn(`❌ Failed to purchase upgrade: ${upgradeId}`);
    }
  };

  return (
    <div style={{
      position: embedded ? 'relative' : 'fixed',
      top: embedded ? 'auto' : 0,
      left: embedded ? 'auto' : 0,
      width: '100%',
      height: '100%',
      backgroundColor: embedded ? 'transparent' : 'rgba(0, 0, 0, 0.95)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: embedded ? 'flex-start' : 'center',
      fontFamily: 'monospace',
      color: '#ffffff',
      zIndex: embedded ? 'auto' : 2000,
      padding: embedded ? '20px' : '40px',
      overflow: 'auto',
    }}>
      {/* Header */}
      <div style={{
        marginBottom: '30px',
        textAlign: 'center',
      }}>
        <h1 style={{
          fontSize: '42px',
          fontWeight: 'bold',
          color: '#ffaa00',
          textShadow: '0 0 20px #ffaa00',
          marginBottom: '10px',
          letterSpacing: '4px',
        }}>
          UPGRADES
        </h1>
        <div style={{
          fontSize: '18px',
          color: '#00ff00',
          marginTop: '10px',
        }}>
          Level {playerLevel} | XP: {playerXP}
        </div>
      </div>

      {/* Upgrades grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '20px',
        maxWidth: '800px',
        marginBottom: '30px',
      }}>
        {upgrades.map((upgrade) => {
          const canAfford = playerXP >= upgrade.cost;
          const isMaxLevel = upgrade.currentLevel >= upgrade.maxLevel;
          const isHovered = hoveredUpgrade === upgrade.id;

          return (
            <div
              key={upgrade.id}
              onMouseEnter={() => {
                setHoveredUpgrade(upgrade.id);
                playUIHover();
              }}
              onMouseLeave={() => setHoveredUpgrade(null)}
              onClick={() => setSelectedUpgrade(upgrade.id)}
              style={{
                padding: '20px',
                backgroundColor: isHovered
                  ? 'rgba(255, 170, 0, 0.2)'
                  : 'rgba(0, 0, 0, 0.7)',
                border: `2px solid ${isHovered ? '#ffaa00' : '#444'}`,
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: isHovered ? '0 0 15px rgba(255, 170, 0, 0.3)' : 'none',
              }}
            >
              {/* Upgrade name */}
              <h3 style={{
                fontSize: '20px',
                color: '#ffaa00',
                marginBottom: '8px',
              }}>
                {upgrade.name}
              </h3>

              {/* Level indicator */}
              <div style={{
                display: 'flex',
                gap: '5px',
                marginBottom: '10px',
              }}>
                {[...Array(upgrade.maxLevel)].map((_, i) => (
                  <div
                    key={i}
                    style={{
                      width: '20px',
                      height: '8px',
                      backgroundColor: i < upgrade.currentLevel ? '#00ff00' : '#333',
                      border: '1px solid #666',
                      borderRadius: '2px',
                    }}
                  />
                ))}
              </div>

              {/* Description */}
              <p style={{
                fontSize: '12px',
                color: '#aaa',
                marginBottom: '15px',
                lineHeight: '1.4',
              }}>
                {upgrade.description}
              </p>

              {/* Purchase button */}
              <button
                disabled={!canAfford || isMaxLevel}
                onClick={(e) => {
                  e.stopPropagation();
                  if (canAfford && !isMaxLevel) {
                    handlePurchase(upgrade.id);
                  }
                }}
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  fontFamily: 'monospace',
                  backgroundColor: isMaxLevel
                    ? 'rgba(100, 100, 100, 0.3)'
                    : canAfford
                      ? 'rgba(0, 255, 0, 0.2)'
                      : 'rgba(255, 0, 0, 0.2)',
                  color: isMaxLevel ? '#666' : canAfford ? '#00ff00' : '#ff0000',
                  border: `2px solid ${isMaxLevel ? '#666' : canAfford ? '#00ff00' : '#ff0000'}`,
                  borderRadius: '4px',
                  cursor: canAfford && !isMaxLevel ? 'pointer' : 'not-allowed',
                  transition: 'all 0.2s ease',
                }}
              >
                {isMaxLevel ? 'MAX LEVEL' : `UPGRADE (${upgrade.cost} XP)`}
              </button>
            </div>
          );
        })}
      </div>

      {/* Close button - only show when not embedded */}
      {!embedded && (
        <button
          onClick={() => {
            playUIClick();
            onClose?.();
          }}
          style={{
            padding: '12px 40px',
            fontSize: '18px',
            fontWeight: 'bold',
            fontFamily: 'monospace',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            color: '#ffffff',
            border: '2px solid #ffffff',
            borderRadius: '6px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            letterSpacing: '2px',
          }}
        >
          CLOSE
        </button>
      )}
    </div>
  );
};
